import ExportButton from '../../components/ExportButton';
import usePageViewState from '../../components/usePageViewState';
import RowActionsMenu from '../../components/RowActionsMenu';
import SearchableFilterList from '../../components/SearchableFilterList';
import { usePermissions } from '../../auth/permissions';
import { styleRecordSheet, downloadWorkbook } from '../../utils/excelExport';
import { addContainerCartonSheet } from './containerReport';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Toast from '../../components/Toast';
import useFieldValidation, { isBlank } from '../../components/useFieldValidation';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Box, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, Container, Eye, FileText, Gauge, House, LayoutGrid, ListChecks, Package, Pencil, Plus, RotateCcw, Ruler, Scale, Search, ShieldCheck, TrainFront, Trash2, X, PackageCheck, CircleX } from 'lucide-react';
import ContainerDraftGoods, { ContainerDraftCartons } from './ContainerDraftGoods';
import CompactBreadcrumb from '../../components/CompactBreadcrumb';
import { createContainer, deleteContainer, emptyLookups, emptySummary, getContainer, getContainerGoods, listContainers, updateContainer } from './containerPlanningService';
import '../warehouses/warehouses.css';
import '../inventory/inventory.css';
import './containers.css';
import '../../components/warehouse-filter.css';
import usePersistentFilters, { validateFilters } from '../../components/usePersistentFilters';

const PAGE_SIZE = 15;
const emptyFilters = { query: '', warehouse_id: '', status: '', date_range: '' };

function formatCount(value) {
  return (Number(value) || 0).toLocaleString('en-US');
}

function formatDecimal(value, digits = 1) {
  return (Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatDate(date) {
  if (!date) return '';
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCreated(date) {
  const label = formatDate(date);
  return label ? `Created ${label}` : '';
}

function profileLabel(item) {
  const named = [item.profile_size, item.profile_type].filter(Boolean).join(' ');
  return named || item.profile_name || '—';
}

function today() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function defaultUnitId(units) {
  const match = (units || []).find(row => /m³|m3|cbm|cubic/i.test(`${row.code || ''} ${row.name || ''}`));
  return match?.id || units?.[0]?.id || '';
}

function defaultProfile(profiles) {
  return (profiles || []).find(row => row.is_default) || (profiles || []).find(row => row.kind === '40hc') || profiles?.[0] || null;
}

function emptyDraft(lookups, draft) {
  if (draft?.id) {
    return {
      internal_reference: draft.internal_reference || '',
      name: draft.name || draft.code || '',
      draft_date: draft.draft_date || today(),
      warehouse_id: draft.warehouse_id || '',
      profile_id: draft.profile_id || 'custom',
      unit_id: draft.unit_id || defaultUnitId(lookups.units),
      capacity: draft.capacity != null ? String(draft.capacity) : '',
      column_capacity: draft.column_capacity != null ? String(draft.column_capacity) : String(draft.capacity ?? ''),
      max_gross_payload: draft.max_gross_payload != null ? String(draft.max_gross_payload) : '',
    };
  }
  const profile = defaultProfile(lookups.profiles);
  return {
    internal_reference: lookups.next_reference || '',
    name: lookups.next_name || '',
    draft_date: today(),
    warehouse_id: lookups.warehouses?.length === 1 ? lookups.warehouses[0].id : '',
    profile_id: profile?.id || 'custom',
    unit_id: defaultUnitId(lookups.units),
    capacity: profile ? String(profile.capacity ?? '') : '',
    column_capacity: profile ? String(profile.capacity ?? '') : '',
    max_gross_payload: profile ? String(profile.max_gross_payload ?? '') : '',
  };
}

async function exportPdf(rows, format = 'pdf') {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Containers', { views: [{ state: 'frozen', ySplit: 1 }] });
  const header = ['Container', 'Date', 'Status', 'Warehouse', 'Profile', 'Capacity (CBM)', 'Filled CBM', 'Remaining CBM', 'Utilization %', 'Cartons', 'Pallets', 'Net Weight (kg)', 'Gross Weight (kg)'];
  const widths = [18, 14, 20, 24, 14, 18, 14, 14, 14, 10, 10, 20, 20];
  const numericColumns = new Set([6, 7, 8, 9, 10, 11, 12, 13]);
  sheet.columns = header.map((label, index) => ({ header: label, width: widths[index] }));
  function excelDate(value) {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))) : value;
  }
  rows.forEach(item => {
    sheet.addRow([
      item.name,
      excelDate(item.draft_date),
      item.status_label,
      item.warehouse_code || item.warehouse_name,
      item.profile_name,
      item.capacity,
      item.filled_cbm,
      item.remaining_cbm,
      item.utilization_percent,
      item.carton_count,
      item.pallet_count,
      item.net_weight,
      item.gross_weight,
    ]);
  });
  styleRecordSheet(sheet, { numericColumns, wrapColumns: new Set([3, 4]) });
  sheet.eachRow((row, rowNumber) => {
    row.eachCell(cell => { cell.alignment = { ...cell.alignment, horizontal: 'left' }; });
    if (rowNumber > 1) row.getCell(2).numFmt = 'dd-mm-yyyy';
  });
  const reports = await Promise.all(rows.map(async container => ({ ...(await getContainerGoods(container.id)), container })));
  addContainerCartonSheet(workbook, reports);
  return downloadWorkbook(workbook, `containers.${format}`);
}

function DraftSelect({ open, onToggle, onClose, onValidate, onSearchChange, value, placeholder, error, label, children, disabled = false, searchable = false }) {
  const ref = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();
  const [query, setQuery] = useState('');
  const [showAllOptions, setShowAllOptions] = useState(false);
  useEffect(() => { if (!open) setShowAllOptions(false); }, [open]);
  const options = searchable ? React.Children.toArray(children).filter(option => filterOptionText(option).toLowerCase().includes(query.trim().toLowerCase())) : children;
  const showMenu = open && (!searchable || showAllOptions || query.trim().length > 0);
  useEffect(() => {
    if (!open) return undefined;
    function handlePointer(event) {
      if (!ref.current?.contains(event.target)) { onValidate?.(); onClose(); }
    }
    function handleKey(event) {
      if (event.key === 'Escape') { onValidate?.(); onClose(); }
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose, onValidate]);

  useEffect(() => {
    if (open && searchable) {
      searchRef.current?.focus({ preventScroll: true });
    }
  }, [open, searchable]);

  return <div className={`cn-select${open ? ' open' : ''}${error ? ' invalid' : ''}`} ref={ref} onClick={event => {
    if (searchable && event.target.closest('.cn-select-trigger') && !event.target.closest('input')) searchRef.current?.focus({ preventScroll: true });
    if (event.target.closest('[role="option"]')) {
      setQuery('');
      onSearchChange?.('');
      ref.current?.querySelector(searchable ? 'input[role="combobox"]' : '.cn-select-trigger')?.focus({ preventScroll: true });
    }
  }} onBlur={event => { if (!ref.current?.contains(event.relatedTarget)) { onValidate?.(); if (open) onClose(); } }}>
    {searchable ? <div className={`cn-control cn-select-trigger${open ? ' open' : ''}${value || query ? '' : ' placeholder'}`}>
      <input ref={searchRef} type="search" role="combobox" aria-label={label} aria-controls={listId} aria-haspopup="listbox" aria-expanded={open} aria-autocomplete="list" autoComplete="off" disabled={disabled}
        value={open ? query : value || query || placeholder} placeholder={open ? `Search ${label.toLowerCase()}...` : undefined}
        onFocus={() => { if (!open && !disabled) onToggle(); }}
        onClick={() => { if (!open && !disabled) onToggle(); }}
        onChange={event => { setQuery(event.target.value); onSearchChange?.(event.target.value); if (!open) onToggle(); }} />
      <button type="button" tabIndex={-1} className="dropdown-list-arrow" disabled={disabled} aria-label="Show all warehouses" aria-haspopup="listbox" aria-expanded={showMenu} aria-controls={listId} onMouseDown={event => event.preventDefault()} onClick={() => { setQuery(''); onSearchChange?.(''); setShowAllOptions(true); if (!open) onToggle(); searchRef.current?.focus({ preventScroll: true }); }}><ChevronDown size={14} strokeWidth={1.8} /></button>
    </div> : <button
      className={`cn-control cn-select-trigger${open ? ' open' : ''}${value ? '' : ' placeholder'}`}
      type="button"
      disabled={disabled}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-invalid={Boolean(error)}
      aria-label={label}
      onClick={() => { if (open) onValidate?.(); onToggle(); }}
    >
      <span className="cn-select-value">{value || placeholder}</span>
      <ChevronDown size={14} strokeWidth={1.8} />
    </button>}
    {showMenu && <div className="cn-select-menu" id={listId} role="listbox">{label === 'Unit of Measurement' ? <SearchableFilterList label="units">{children}</SearchableFilterList> : options}{searchable && !options.length && <p className="cn-select-empty">No matching warehouses.</p>}</div>}
  </div>;
}

function filterOptionText(node) {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (!React.isValidElement(node)) return '';
  return React.Children.toArray(node.props.children).map(filterOptionText).join(' ');
}

function FilterMenu({ open, onToggle, onClose, icon, label, value, className = '', searchable = false, children }) {
  const ref = useRef(null);
  const inputRef = useRef(null);
  const listId = useId();
  const [query, setQuery] = useState('');
  const [showAllOptions, setShowAllOptions] = useState(false);
  useEffect(() => { if (!open) setShowAllOptions(false); }, [open]);
  const options = searchable ? React.Children.toArray(children).filter(option => filterOptionText(option).toLowerCase().includes(query.trim().toLowerCase())) : children;
  useEffect(() => {
    if (!open) return undefined;
    function handlePointer(event) {
      if (!ref.current?.contains(event.target)) onClose();
    }
    function handleKey(event) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !searchable) return;
    setQuery('');
    inputRef.current?.focus({ preventScroll: true });
  }, [open, searchable]);

  function handleKeyDown(event) {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !open) return;
    const items = Array.from(ref.current?.querySelectorAll('[role="option"]:not(:disabled)') || []);
    if (!items.length) return;
    event.preventDefault();
    event.stopPropagation();
    const current = items.indexOf(document.activeElement);
    const next = current < 0 ? (event.key === 'ArrowDown' ? 0 : items.length - 1)
      : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next].focus();
  }

  return <div className={`wh-menu ${className}`} ref={ref} onClick={event => {
    if (searchable && !event.target.closest('input')) inputRef.current?.focus({ preventScroll: true });
  }} onKeyDown={handleKeyDown}>
    {searchable ? <div className={`wh-menu-trigger${open ? ' open' : ''}`}>
      {icon}
      <span className="wh-menu-label">{label}</span>
      <input ref={inputRef} className="wh-menu-search-input" type="search" role="combobox" aria-label={`${label} filter`} aria-controls={listId} aria-haspopup="listbox" aria-expanded={open} aria-autocomplete="list" autoComplete="off"
        value={open ? query : value} placeholder={open ? `Search ${label.toLowerCase()}...` : undefined}
        onFocus={() => { if (!open) { setQuery(''); onToggle(); } }}
        onClick={() => { if (!open) { setQuery(''); onToggle(); } }}
        onChange={event => { setQuery(event.target.value); if (!open) onToggle(); }} />
      <button type="button" tabIndex={-1} className="dropdown-list-arrow" aria-label={`Show all ${label.toLowerCase()} options`} aria-haspopup="listbox" aria-expanded={open} aria-controls={listId} onMouseDown={event => event.preventDefault()} onClick={() => { setQuery(''); setShowAllOptions(true); if (!open) onToggle(); inputRef.current?.focus({ preventScroll: true }); }}><ChevronDown size={14} strokeWidth={1.8} className="wh-menu-caret" /></button>
    </div> : <button className={`wh-menu-trigger${open ? ' open' : ''}`} type="button" aria-haspopup="listbox" aria-expanded={open} onClick={onToggle}>
      {icon}
      <span className="wh-menu-label">{label}</span>
      <span className="wh-menu-value">{value}</span>
      <ChevronDown size={14} strokeWidth={1.8} className="wh-menu-caret" />
    </button>}
    {open && (!searchable || showAllOptions || query.trim()) && <div className="wh-menu-panel" id={listId} role="listbox">{options}{searchable && !options.length && <p className="wh-menu-empty">No matching warehouses.</p>}</div>}
  </div>;
}

function validateForm(form) {
  const next = {};
  if (isBlank(form.name)) next.name = 'Container name is required.';
  if (isBlank(form.draft_date)) next.draft_date = 'Date is required.';
  if (isBlank(form.warehouse_id)) next.warehouse_id = 'Warehouse is required.';
  if (isBlank(form.column_capacity)) next.column_capacity = 'Volumetric capacity is required.';
  else if (!(Number(form.column_capacity) > 0)) next.column_capacity = 'Volumetric capacity must be greater than 0.';
  return next;
}

export function ContainerDraftForm({ lookups, draft, saving, mode, onCancel, onSubmit, onToast }) {
  const isEdit = mode === 'edit' && Boolean(draft?.id);
  const [form, setForm] = useState(() => emptyDraft(lookups, draft));
  const { errors, setErrors, touchField } = useFieldValidation(form, validateForm);
  const [openSelect, setOpenSelect] = useState(null);
  const [warehouseQuery, setWarehouseQuery] = useState('');
  const presets = lookups.profiles || [];
  const selectedProfile = presets.find(row => String(row.id) === String(form.profile_id));
  const selectedWarehouse = (lookups.warehouses || []).find(row => String(row.id) === String(form.warehouse_id));
  const warehouseError = warehouseQuery.trim() && errors.warehouse_id === 'Warehouse is required.' ? undefined : errors.warehouse_id;
  const selectedUnit = (lookups.units || []).find(row => String(row.id) === String(form.unit_id));
  const isCustom = !selectedProfile;

  function closeSelect() {
    setOpenSelect(null);
  }

  function toggleSelect(name) {
    setOpenSelect(current => (current === name ? null : name));
  }

  function update(name, value) {
    const limitField = { capacity: 'capacity', column_capacity: 'capacity', max_gross_payload: 'max_gross_payload' }[name];
    if (selectedProfile && limitField && value !== '' && Number.isFinite(Number(value)) && Number(value) > Number(selectedProfile[limitField])) {
      const label = { capacity: 'Capacity', column_capacity: 'Volumetric capacity', max_gross_payload: 'Max gross payload' }[name];
      const unit = name === 'max_gross_payload' ? 'kg' : 'm³';
      onToast?.(`${label} is greater than the selected container limit of ${selectedProfile[limitField]} ${unit}. Value reset to 0.`, 'error', 'Value greater than limit');
      value = '0';
    }
    setForm(current => ({ ...current, [name]: value }));
    setErrors(current => ({ ...current, [name]: undefined }));
  }

  function selectPreset(profile) {
    setOpenSelect(null);
    if (!profile) {
      setForm(current => ({ ...current, profile_id: 'custom' }));
      return;
    }
    setForm(current => ({
      ...current,
      profile_id: profile.id,
      capacity: String(profile.capacity ?? ''),
      column_capacity: String(profile.capacity ?? ''),
      max_gross_payload: String(profile.max_gross_payload ?? ''),
    }));
  }

  function handleSubmit(event) {
    event.preventDefault();
    const next = validateForm(form);
    if (selectedProfile && (Number(form.capacity) > Number(selectedProfile.capacity) || Number(form.column_capacity) > Number(selectedProfile.capacity) || Number(form.max_gross_payload) > Number(selectedProfile.max_gross_payload))) {
      onToast?.('Measurements are greater than the selected container limits. Adjust the values before saving.', 'error', 'Value greater than limit');
      return;
    }
    if (Object.keys(next).length) {
      if (isBlank(form.warehouse_id)) setWarehouseQuery('');
      setErrors(next);
      return;
    }
    setErrors(next);
    onSubmit({
      name: form.name.trim(),
      draft_date: form.draft_date,
      warehouse_id: Number(form.warehouse_id),
      container_profile_id: selectedProfile ? Number(selectedProfile.id) : null,
      unit_of_measurement_id: form.unit_id ? Number(form.unit_id) : null,
      capacity: Number(form.column_capacity),
      column_capacity: Number(form.column_capacity),
      max_gross_payload: Number(form.max_gross_payload || 0),
    });
  }

  return <form className="cn-create" onSubmit={handleSubmit}>
    <div className="cn-create-head">
      <div>
        <CompactBreadcrumb parent="Containers" current={isEdit ? 'Edit Container Draft' : 'Create Container Draft'} onNavigate={onCancel} disabled={saving} />
        <h1>{isEdit ? 'Edit Container Draft' : 'Create Container Draft'}</h1>
      </div>
      <div className="cn-create-actions">
        <button className="inv-export" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="wh-add cn-save" type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save & Proceed to Add Inventory'}
        </button>
      </div>
    </div>

    <article className="cn-card">
      <header className="cn-card-head">
        <h2><LayoutGrid size={15} strokeWidth={1.8} />Basic Information</h2>
        <span>STEP 1 OF 2</span>
      </header>
      <div className="cn-form-grid">
        <label className={`cn-field${errors.draft_date ? ' invalid' : ''}`}>
          <span>Date <i>*</i></span>
          <div className="cn-control">
            <input autoFocus onBlur={() => touchField('draft_date')} type="date" value={form.draft_date} onChange={event => update('draft_date', event.target.value)} aria-invalid={Boolean(errors.draft_date)} />
          </div>
          {errors.draft_date && <small>{errors.draft_date}</small>}
        </label>
        <label className={`cn-field${errors.name ? ' invalid' : ''}`}>
          <span>Container Name <i>*</i></span>
          <div className="cn-control">
            <input onBlur={() => touchField('name')} placeholder="Enter container name" value={form.name} onChange={event => update('name', event.target.value)} aria-label="Container Name" aria-invalid={Boolean(errors.name)} />
          </div>
          {errors.name && <small>{errors.name}</small>}
        </label>
        <div className={`cn-field${warehouseError ? ' invalid' : ''}`}>
          <span>Warehouse / Origin Facility <i>*</i></span>
          <DraftSelect
            searchable
            open={openSelect === 'warehouse'}
            onToggle={() => toggleSelect('warehouse')}
            onClose={closeSelect}
            onSearchChange={setWarehouseQuery}
            value={selectedWarehouse?.name || ''}
            placeholder="Select warehouse"
            error={warehouseError} onValidate={() => touchField('warehouse_id')}
            label="Warehouse / Origin Facility"
          >
            {(lookups.warehouses || []).map(row => {
              const selected = String(form.warehouse_id) === String(row.id);
              return <button key={row.id} className={`cn-select-option${selected ? ' selected' : ''}`} type="button" role="option" aria-selected={selected} onClick={() => { update('warehouse_id', row.id); closeSelect(); }}>
                {row.name}
              </button>;
            })}
          </DraftSelect>
          {warehouseError && <small>{warehouseError}</small>}
        </div>
      </div>
    </article>

    <article className="cn-card">
      <header className="cn-card-head">
        <h2><Package size={15} strokeWidth={1.8} />Container Format</h2>
      </header>
      <p className="cn-card-sub">Standard Format Presets (Quick Select)</p>
      <div className="cn-presets" role="radiogroup" aria-label="Container format">
        {presets.map(row => {
          const selected = String(form.profile_id) === String(row.id);
          return <button key={row.id} className={`cn-preset${selected ? ' selected' : ''}`} type="button" role="radio" aria-checked={selected} onClick={() => selectPreset(row)}>
            <span className="cn-radio" aria-hidden="true" />
            <strong>{row.label}{row.is_default ? <em>DEFAULT</em> : null}</strong>
            <small>{formatDecimal(row.capacity)} m³ • {formatCount(row.max_gross_payload)} kg</small>
          </button>;
        })}
        <button className={`cn-preset${isCustom ? ' selected' : ''}`} type="button" role="radio" aria-checked={isCustom} onClick={() => selectPreset(null)}>
          <span className="cn-radio" aria-hidden="true" />
          <strong>Custom Specification</strong>
          <small>Manual payload & volume override</small>
        </button>
      </div>
    </article>

    <article className="cn-card">
      <header className="cn-card-head">
        <h2><Ruler size={15} strokeWidth={1.8} />Capacity & Measurement</h2>
      </header>
      <div className="cn-measure">
        <div className="cn-field">
          <span>Unit of Measurement</span>
          <DraftSelect
            open={openSelect === 'unit'}
            disabled={!isCustom}
            onToggle={() => toggleSelect('unit')}
            onClose={closeSelect}
            value={selectedUnit?.label || selectedUnit?.name || ''}
            placeholder="Select unit"
            label="Unit of Measurement"
          >
            {(lookups.units || []).map(row => {
              const selected = String(form.unit_id) === String(row.id);
              return <button key={row.id} className={`cn-select-option${selected ? ' selected' : ''}`} type="button" role="option" aria-selected={selected} onClick={() => { update('unit_id', row.id); closeSelect(); }}>
                {row.label || row.name}
              </button>;
            })}
          </DraftSelect>
        </div>
        <label className={`cn-field${errors.column_capacity ? ' invalid' : ''}`}>
          <span>Volumetric Capacity</span>
          <div className="cn-control cn-suffix">
            <input disabled={!isCustom} onBlur={() => touchField('column_capacity')} inputMode="decimal" placeholder="Enter volumetric capacity" value={form.column_capacity} onChange={event => update('column_capacity', event.target.value)} aria-label="Volumetric Capacity" />
            <b>m³</b>
          </div>
          {errors.column_capacity && <small>{errors.column_capacity}</small>}
        </label>
        <label className="cn-field">
          <span>Max Gross Payload</span>
          <div className="cn-control cn-suffix">
            <input disabled={!isCustom} onBlur={() => touchField('max_gross_payload')} inputMode="decimal" placeholder="Enter max gross payload" value={form.max_gross_payload} onChange={event => update('max_gross_payload', event.target.value)} aria-label="Max Gross Payload" />
            <b>kg</b>
          </div>
        </label>
      </div>
    </article>
  </form>;
}

export default function ContainerPlanningPage() {
  const { canWrite } = usePermissions();
  const writable = canWrite('ContainerPlanning');
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(emptySummary);
  const [lookups, setLookups] = useState(emptyLookups);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters, reconcileFilters] = usePersistentFilters('containerPlanningFilters', emptyFilters, {
    searchParams, setSearchParams: (params, options) => setSearchParams(params, { ...options, state: location.state }),
    navigationFilters: location.state?.containerPlanningFilters,
    restoreWarehouseEarly: true,
    urlKeys: { warehouse_id: 'warehouseId', status: 'status', query: 'query', date_range: 'date_range' },
  });
  const [page, setPage] = useState(1);
  const pageFilters = useRef(filters);
  const containersLoaded = useRef(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [detail, setDetail] = usePageViewState('detail', null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [workflow, setWorkflow] = usePageViewState('workflow', () => writable && location.state?.createContainerDraft ? { step: 1, draft: null } : null);
  const [reviewRecord, setReviewRecord] = usePageViewState('reviewRecord', null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const start = total ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(currentPage * PAGE_SIZE, total);
  const kpis = [
    [FileText, 'blue', 'Total Draft', summary.total_draft],
    [FileText, 'blue', 'Planned', summary.planned],
    [Package, 'green', 'Ready to Ship', summary.ready_to_ship],
    [TrainFront, 'violet', 'Shipped', summary.shipped],
    [ShieldCheck, 'amber', 'Custom Clearance', summary.customs_clearance],
    [PackageCheck, 'green', 'Delivered', summary.delivered],
    [CircleX, 'blue', 'Cancelled', summary.cancelled],
  ];

  function showToast(message, tone = 'success', title) {
    clearTimeout(toastTimer.current);
    setToast({ message, tone, title });
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }

  function closeMenus() {
    setOpenMenu(null);

  }

  function updateFilter(name, value) {
    setFilters(current => ({ ...current, [name]: String(value) }));
    setPage(1);
    closeMenus();
  }

  const applyLoaded = useCallback(data => {
    const options = { ...emptyLookups, ...data.lookups };
    setLookups(options);
    if (reconcileFilters(values => validateFilters(values, emptyFilters, {
      warehouse_id: options.warehouses.map(row => row.id),
      status: [...options.statuses.map(row => row.key), 'Cancelled'],
      date_range: options.date_ranges.map(row => row.key),
    }, true))) return;
    setRecords(data.containers);
    setTotal(data.total);
    setSummary(data.summary);
  }, [reconcileFilters]);

  async function loadPage(nextFilters = filters, nextPage = currentPage) {
    const data = await listContainers({ ...nextFilters, page: nextPage, pageSize: PAGE_SIZE });
    applyLoaded(data);
  }

  useEffect(() => {
    if (pageFilters.current !== filters && page !== 1) {
      setPage(1);
      return undefined;
    }
    pageFilters.current = filters;
    let cancelled = false;
    async function load() {
      try {
        const data = await listContainers({ ...filters, page, pageSize: PAGE_SIZE }, { background: containersLoaded.current });
        if (cancelled) return;
        applyLoaded(data);
        containersLoaded.current = true;
      } catch (error) {
        if (!cancelled) {
          setRecords([]);
          setTotal(0);
          showToast(error.message, 'error');
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [filters, page, applyLoaded]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  useEffect(() => {

  }, [filters, currentPage]);

  useEffect(() => {
    if (!deleteItem) return undefined;
    function handleKey(event) {
      if (event.key === 'Escape' && !deleting) closeDelete();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [deleteItem, deleting]);

  function openRecordReview(item) {

    setOpenMenu(null);
    setReviewRecord(item);
  }

  async function openEdit(selected) {
    if (!writable) return;

    if (!selected) return;
    try {
      const loaded = await getContainer(selected.id);
      setWorkflow({ step: 2, draft: loaded || selected, mode: 'edit' });
    } catch (error) {
      setWorkflow({ step: 2, draft: selected, mode: 'edit' });
      showToast(error.message, 'error');
    }
  }

  function openDelete(selected) {
    if (!writable) return;

    if (selected) setDeleteItem(selected);
  }

  function closeDelete() {
    if (deleting) return;
    setDeleteItem(null);
  }

  async function confirmDelete() {
    if (!writable) return;
    if (deleting || !deleteItem) return;
    setDeleting(true);
    try {
      await deleteContainer(deleteItem.id);
      setDeleteItem(null);
      await loadPage(filters, currentPage);
      showToast('Container deleted.');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setDeleteItem(null);
      setDeleting(false);
    }
  }

  async function saveDraft(payload) {
    if (!writable) return;
    if (saving) return;
    setSaving(true);
    try {
      if (workflow?.draft?.id) {
        const updated = await updateContainer(workflow.draft.id, payload);
        setWorkflow({ step: 2, draft: updated, mode: workflow.mode });
        showToast(workflow.mode === 'edit' ? 'Container draft updated.' : 'Container draft saved.');
        return;
      }
      const created = await createContainer(payload);
      setWorkflow({ step: 2, draft: created });
      showToast('Container draft created.');
    } catch (error) {
      if (workflow?.draft?.id && (error.status === 403 || error.status === 404)) {
        setWorkflow(null);
        showToast("You don't have permission to edit this container.", 'error');
      } else showToast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const selectedWarehouse = lookups.warehouses.find(row => String(row.id) === String(filters.warehouse_id));
  const selectedStatus = lookups.statuses.find(row => row.key === filters.status) || (filters.status === 'Cancelled' ? { key: 'Cancelled', label: 'Cancelled (historical)' } : undefined);
  const selectedDate = lookups.date_ranges.find(row => row.key === filters.date_range) || lookups.date_ranges[0];

  if (reviewRecord) return <>
    <ContainerDraftCartons key={reviewRecord.id} draft={reviewRecord} lookups={lookups} readOnly
      onClose={() => setReviewRecord(null)} onToast={showToast} />
    <Toast toast={toast} onClose={() => setToast(null)} />
  </>;

  if (writable && workflow?.step === 1) {
    return <section className="wh-page inv-page cn-page">
      <ContainerDraftForm key={workflow.draft?.id || 'new'} lookups={lookups} draft={workflow.draft} saving={saving} mode={workflow.mode} onCancel={() => setWorkflow(null)} onSubmit={saveDraft} onToast={showToast} />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </section>;
  }

  if (writable && workflow?.step === 2 && workflow.draft?.id) {
    return <>
      <ContainerDraftGoods
        draft={workflow.draft}
        onBack={() => setWorkflow({ step: 1, draft: workflow.draft, mode: workflow.mode })}
        mode={workflow.mode}
        onClose={() => { setWorkflow(null); loadPage(filters, currentPage); }}
        onContinue={() => setWorkflow({ step: 3, draft: workflow.draft, mode: workflow.mode })}
        onToast={showToast}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>;
  }

  if (writable && workflow?.step === 3 && workflow.draft?.id) {
    return <>
      <ContainerDraftCartons
        draft={workflow.draft}
        lookups={lookups}
        onBack={() => setWorkflow({ step: 2, draft: workflow.draft, mode: workflow.mode })}
        onSetup={() => setWorkflow({ step: 1, draft: workflow.draft, mode: workflow.mode })}
        mode={workflow.mode}
        onClose={() => { setWorkflow(null); loadPage(filters, currentPage); }}
        onToast={showToast}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>;
  }

  return <section className="wh-page inv-page cn-page cn-list-page">
    <div className="wh-title">
      <div>
        {location.state?.fromDashboard && <CompactBreadcrumb parent="Dashboard" current="Containers" onNavigate={() => navigate('/dashboard')} />}
        <h1>Containers</h1>
      </div>
      <div className="wh-title-actions">
        <ExportButton onExport={format => exportPdf(records, format).catch(error => showToast(error.message || 'Unable to export containers.', 'error'))}>Export</ExportButton>
        <button className="wh-add" type="button" disabled={!writable} onClick={() => setWorkflow({ step: 1, draft: null })}>
          <Plus size={14} />New Container Draft
        </button>
      </div>
    </div>

    <div className="wh-kpis cn-kpis">
      {kpis.map(([Icon, tone, label, value]) => <article className="wh-kpi" key={label}>
        <span className={`wh-kpi-icon ${tone}`}><Icon size={16} strokeWidth={1.8} /></span>
        <div className="wh-kpi-copy">
          <span>{label}</span>
          <strong>{formatCount(value)}</strong>
        </div>
      </article>)}
    </div>

    <div className="wh-panel">
      <div className="wh-filters inv-filters cn-filters">
      <label className="wh-search">
        <Search size={14} />
        <input type="search" placeholder="Search containers..." value={filters.query} onChange={event => updateFilter('query', event.target.value)} />
      </label>
      <FilterMenu
        className="warehouse-filter"
        searchable
        open={openMenu === 'warehouse'}
        onToggle={() => { setOpenMenu(openMenu === 'warehouse' ? null : 'warehouse'); }}
        onClose={closeMenus}
        icon={<House size={14} strokeWidth={1.8} />}
        label="Warehouse"
        value={selectedWarehouse?.name || 'Select Warehouse'}
      >
        {lookups.warehouses.map(row => {
            const selected = String(filters.warehouse_id || '') === String(row.id || '');
            return <button key={row.id || 'all'} className={`wh-menu-item${selected ? ' selected' : ''}`} type="button" role="option" aria-selected={selected} onClick={() => updateFilter('warehouse_id', row.id || '')}>
              <span>{row.name}</span>
              {selected && <Check size={14} strokeWidth={2} />}
            </button>;
          })}
      </FilterMenu>
      <FilterMenu
        open={openMenu === 'status'}
        onToggle={() => { setOpenMenu(openMenu === 'status' ? null : 'status'); }}
        onClose={closeMenus}
        icon={<Package size={14} strokeWidth={1.8} />}
        label="Status"
        value={selectedStatus?.label || 'All Status'}
      >
        <div className="wh-menu-list">
          {[{ key: '', label: 'All Status' }, ...lookups.statuses].map(row => {
            const selected = (filters.status || '') === row.key;
            return <button key={row.key || 'all'} className={`wh-menu-item${selected ? ' selected' : ''}`} type="button" role="option" aria-selected={selected} onClick={() => updateFilter('status', row.key)}>
              <span>{row.label}</span>
              {selected && <Check size={14} strokeWidth={2} />}
            </button>;
          })}
        </div>
      </FilterMenu>
      <FilterMenu
        open={openMenu === 'date'}
        onToggle={() => { setOpenMenu(openMenu === 'date' ? null : 'date'); }}
        onClose={closeMenus}
        icon={<CalendarDays size={14} strokeWidth={1.8} />}
        label="Date"
        value={selectedDate?.label || 'All Dates'}
      >
        <div className="wh-menu-list">
          {lookups.date_ranges.map(row => {
            const selected = (filters.date_range || '') === row.key;
            return <button key={row.key || 'all'} className={`wh-menu-item${selected ? ' selected' : ''}`} type="button" role="option" aria-selected={selected} onClick={() => updateFilter('date_range', row.key)}>
              <span>{row.label}</span>
              {selected && <Check size={14} strokeWidth={2} />}
            </button>;
          })}
        </div>
      </FilterMenu>
      <button className="wh-filter-btn" type="button" onClick={() => { setFilters({ ...emptyFilters, warehouse_id: String(lookups.warehouses[0]?.id || '') }); setPage(1); closeMenus(); }}><RotateCcw size={14} strokeWidth={1.7} />Reset</button>
    </div>
      <div className="table-scroll-wrapper">
        <table className="wh-table cn-table">
          <thead>
            <tr>
              <th className="wh-actions-heading" aria-label="Actions" title="Actions"><ListChecks size={16} aria-hidden="true" /></th>
              <th>Container Name / Date / Code</th>
              <th>Capacity</th>
              <th>Filled Capacity</th>
              <th>Total Carton</th>
              <th>Total N Weight</th>
              <th>Total G Weight</th>
              <th>Volumetric Cap.</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {records.map(item => (
              <tr key={item.id} className="cn-record-row">
                <td className="wh-actions-cell" onClick={event => event.stopPropagation()}>
                  <RowActionsMenu className="inv-icon-action" label={`Actions for container ${item.name}`} actions={[
                    { label: 'View', icon: Eye, onClick: () => openRecordReview(item) },
                    { label: 'Edit', icon: Pencil, disabled: !writable, onClick: () => openEdit(item) },
                    { label: 'Delete', icon: Trash2, danger: true, disabled: !writable, onClick: () => openDelete(item) },
                  ]} />
                </td>
                <td>
                  <div className="cn-name">
                    <span>{item.name || '—'}</span>
                    {item.draft_date ? <small>{formatCreated(item.draft_date)}</small> : null}
                    {item.code ? <small className="cn-code">{item.code}</small> : null}
                  </div>
                </td>
                <td>
                  <div className="cn-capacity">
                    <strong>{item.profile_size || item.profile_name || '—'}</strong>
                    {item.profile_type ? <small>{item.profile_type}</small> : null}
                  </div>
                </td>
                <td>
                  <div className="cn-filled">
                    <strong>{formatDecimal(item.filled_cbm)} m³</strong>
                    <small>{formatDecimal(item.capacity)}</small>
                    <span>{formatDecimal(item.utilization_percent)}%</span>
                    <div className="cn-bar" aria-hidden="true">
                      <i style={{ width: `${Math.min(100, Math.max(0, item.utilization_percent))}%` }} />
                    </div>
                  </div>
                </td>
                <td>
                  <div className="cn-stack">
                    <strong>{formatCount(item.carton_count)}</strong>
                    <small>ctns</small>
                    {item.pallet_count > 0 ? <em>{formatCount(item.pallet_count)} Pallets</em> : null}
                  </div>
                </td>
                <td>
                  <div className="cn-stack">
                    <strong>{formatCount(item.net_weight)}</strong>
                    <small>kg</small>
                  </div>
                </td>
                <td>
                  <div className="cn-stack">
                    <strong>{formatCount(item.gross_weight)}</strong>
                    <small>kg</small>
                  </div>
                </td>
                <td>
                  <div className="cn-volume">
                    <strong>{formatDecimal(item.utilization_percent)}% utilized</strong>
                    <small>{formatDecimal(item.remaining_cbm)} m³ rem.</small>
                  </div>
                </td>
                <td>
                  <span className={`cn-status ${item.status_tone}`}>{item.status_label}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!records.length && <p className="cn-empty">No containers found.</p>}

      <div className="wh-pager">
        <p>Showing {start}–{end} of {total} Containers</p>
        <div>
          <button type="button" disabled={currentPage === 1} onClick={() => { setPage(currentPage - 1); }}><ChevronLeft size={14} />Previous</button>
          {Array.from({ length: pageCount }, (_, index) => <button key={index + 1} type="button" className={`wh-page-number${currentPage === index + 1 ? ' current' : ''}`} aria-current={currentPage === index + 1 ? 'page' : undefined} onClick={() => { setPage(index + 1); }}>{index + 1}</button>)}
          <button type="button" disabled={currentPage === pageCount} onClick={() => { setPage(currentPage + 1); }}>Next<ChevronRight size={14} /></button>
        </div>
      </div>
    </div>


    {detail && <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) setDetail(null); }}>
      <div className="wh-modal cn-modal" role="dialog" aria-modal="true" aria-labelledby="cn-detail-title">
        <header className="cn-view-head">
          <h2 id="cn-detail-title">View Container</h2>
          <button className="wh-form-close" type="button" aria-label="Close view" onClick={() => setDetail(null)}><X size={16} strokeWidth={1.8} /></button>
        </header>
        <dl className="cn-detail">
          {[
            [Box, 'Container', detail.item.internal_reference || detail.item.name || '—'],
            [LayoutGrid, 'Code', detail.item.code || '—'],
            [CalendarDays, 'Created', detail.item.draft_date ? formatDate(detail.item.draft_date) : '—'],
            [House, 'Warehouse', detail.item.warehouse_name || '—'],
            [Box, 'Capacity', `${profileLabel(detail.item)} · ${formatDecimal(detail.item.capacity)} m³`],
            [Box, 'Filled', `${formatDecimal(detail.item.filled_cbm)} m³ · ${formatDecimal(detail.item.utilization_percent)}%`],
            [Gauge, 'Remaining', `${formatDecimal(detail.item.remaining_cbm)} m³`],
            [Package, 'Cartons', `${formatCount(detail.item.carton_count)} ctns`],
            [Container, 'Pallets', formatCount(detail.item.pallet_count)],
            [Scale, 'Net Weight', `${formatCount(detail.item.net_weight)} kg`],
            [Scale, 'Gross Weight', `${formatCount(detail.item.gross_weight)} kg`],
            [FileText, 'Status', detail.item.status_label || '—'],
          ].map(([Icon, label, value]) => (
            <div className="cn-detail-item" key={label}>
              <Icon size={16} strokeWidth={1.7} />
              <div>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            </div>
          ))}
        </dl>
        <div className="wh-modal-actions">
          <button className="wh-btn-ghost cn-view-close" type="button" onClick={() => setDetail(null)} autoFocus>Close</button>
        </div>
      </div>
    </div>}

    {writable && deleteItem && <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeDelete(); }}>
      <div className="wh-modal wh-modal-confirm" role="dialog" aria-modal="true" aria-labelledby="cn-delete-title">
        <h2 id="cn-delete-title">Delete Container?</h2>
        <p className="wh-modal-copy">Are you sure you want to delete this container?</p>
        <div className="wh-delete-target">
          <strong>{deleteItem.name || deleteItem.internal_reference}</strong>
          <span>{[deleteItem.code, deleteItem.warehouse_name].filter(Boolean).join(' · ')}</span>
        </div>
        <div className="wh-modal-actions">
          <button className="wh-btn-ghost" type="button" onClick={closeDelete} disabled={deleting} autoFocus>Cancel</button>
          <button className="wh-btn-danger" type="button" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
        </div>
      </div>
    </div>}

    <Toast toast={toast} onClose={() => setToast(null)} />
  </section>;
}
